import React from "react";
import { Badge, Box, Menu, Text, useMantineTheme } from "@mantine/core";
import { HiPencil } from "react-icons/hi";
import Tooltip from "components/Tooltip/Tooltip";
import GoalMenuDropdown from "components/Workspace/Meeting/StrategicGoal/GoalMenuDropdown";
import { buildGoalTooltip } from "utils/strategicGoals";

// The whole row is a button that opens the meeting. Anything that happens
// inside this cell — a click on the pencil, typing in the search box, Enter
// on a menu item — must not reach it. The dropdown is portaled, but React
// still bubbles its events through the tree, so one wrapper catches both.
const swallow = (event) => event.stopPropagation();

const MeetingDistGoalCell = ({
  goal,
  isPending,
  canEdit,
  showReadOnlyNote,
  goals,
  onSelect,
}) => {
  const theme = useMantineTheme();
  const goalName = goal?.goal || "No goal";
  const dotColor = theme.colors[goal?.color]?.[5] || theme.colors.gray[4];

  const label = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: dotColor,
          flexShrink: 0,
        }}
      />
      <Text
        size="xs"
        color={isPending ? "blue" : "dimmed"}
        weight={isPending ? 600 : undefined}
        truncate
        title={goalName}
      >
        {goalName}
      </Text>
      {isPending && (
        <Badge size="xs" color="blue" variant="light" sx={{ flexShrink: 0 }}>
          unsaved
        </Badge>
      )}
      {canEdit && (
        <HiPencil
          size={13}
          aria-hidden
          style={{ flexShrink: 0, color: theme.colors.gray[5] }}
        />
      )}
    </Box>
  );

  if (!canEdit) {
    if (!showReadOnlyNote) return label;

    const { label: tooltipLabel } = buildGoalTooltip({
      goal: goal?.goal,
      reason: goal?.reason,
      canEdit: false,
    });
    return (
      // withinPortal: the table clips anything rendered inline.
      <Tooltip
        withinPortal
        label={tooltipLabel}
        sx={{ maxWidth: "22rem", whiteSpace: "normal", textAlign: "left" }}
      >
        {label}
      </Tooltip>
    );
  }

  return (
    <Box onClick={swallow} onKeyDown={swallow} sx={{ minWidth: 0 }}>
      <Menu withinPortal shadow="md" position="bottom-start">
        <Menu.Target>
          <Box
            role="button"
            tabIndex={0}
            aria-label={`Change strategic goal (${goalName})`}
            sx={{ cursor: "pointer", minWidth: 0 }}
          >
            {label}
          </Box>
        </Menu.Target>
        <GoalMenuDropdown goals={goals} onSelect={onSelect} />
      </Menu>
    </Box>
  );
};

export default MeetingDistGoalCell;
